import { DEFAULT_RANGE, WEEKDAY_LABELS } from "@/helpers/availability";
import { WeeklyDay } from "@/interfaces/Availability";
import { FC } from "react";
import { Form } from "react-bootstrap";
import RangeListEditor from "./RangeListEditor";

interface IProps {
  value: WeeklyDay[];
  onChange: (value: WeeklyDay[]) => void;
  errors: Record<string, string>;
  readOnly?: boolean;
}

/** Du mardi au vendredi : ce que recopie « Recopier le lundi ». */
const WORKDAYS_AFTER_MONDAY = [2, 3, 4, 5];

/**
 * La semaine type : une ligne par jour, un interrupteur pour ouvrir ou fermer
 * la journée, ses plages à la suite.
 */
const WeeklyScheduleEditor: FC<IProps> = ({ value, onChange, errors, readOnly = false }) => {
  const setRanges = (index: number, ranges: WeeklyDay["ranges"]) =>
    onChange(value.map((d, i) => (i === index ? { ...d, ranges } : d)));

  const monday = value.find((d) => d.weekday === 1);
  const copyMonday = () =>
    onChange(
      value.map((d) =>
        WORKDAYS_AFTER_MONDAY.includes(d.weekday)
          ? { ...d, ranges: (monday?.ranges ?? []).map((r) => ({ ...r })) }
          : d,
      ),
    );

  return (
    <section aria-labelledby="weekly-schedule-title">
      <div className="fx-availability__head">
        <h3 id="weekly-schedule-title" className="fx-availability__title">
          Semaine type
        </h3>
        {!readOnly && !!monday?.ranges.length && (
          <button
            type="button"
            className="fx-availability__link"
            title="Applique les horaires du lundi au mardi, mercredi, jeudi et vendredi"
            onClick={copyMonday}
          >
            Recopier le lundi du mardi au vendredi
          </button>
        )}
      </div>
      <p className="fx-availability__hint">
        {readOnly
          ? "Les horaires habituels, jour par jour."
          : "Les horaires habituels, jour par jour. Une deuxième plage (le « + ») marque une pause."}
      </p>

      <ul className="fx-week">
        {value.map((day, index) => {
          const label = WEEKDAY_LABELS[day.weekday - 1];
          const open = day.ranges.length > 0;
          return (
            <li key={day.weekday} className={`fx-day ${open ? "" : "fx-day--closed"}`}>
              <span className="fx-day__name">{label}</span>
              {readOnly ? (
                <span aria-hidden />
              ) : (
                <Form.Check
                  type="switch"
                  id={`weekly-open-${day.weekday}`}
                  className="fx-day__switch"
                  aria-label={`${label} : ouvert`}
                  checked={open}
                  onChange={(event) =>
                    setRanges(index, event.target.checked ? [{ ...DEFAULT_RANGE }] : [])
                  }
                />
              )}
              <div className="fx-day__body">
                <RangeListEditor
                  ranges={day.ranges}
                  onChange={(ranges) => setRanges(index, ranges)}
                  errorKey={`weekly.${index}.ranges`}
                  errors={errors}
                  context={`du ${label.toLowerCase()}`}
                  addWhenEmpty={false}
                  readOnly={readOnly}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default WeeklyScheduleEditor;
